import MediaMatcher from "../Lib/MediaMatcher";
import { radToBetween, random, randomFraction, updateCanvasSize, valueForFrame } from "./Utilities";

// Settings
const settings = {
    debug: false,
    center: {
        x: null,
        y: null,
    },
    elapsed: 0,
    newDotEveryNthFrame: 60,
    newDotNthFrame: {
        from: 260,
        to: 360,
    },
    mainCircle: {
        radius: null,
        speed: 950,
        pos: { x: 0, y: 0 },
    },
    dot: {
        radius: 4,
        animation: 2000,
        stay: 1000,
    },
    ghostCircle: {
        radiusMin: 100,
        radiusMax: null,
        animation: 825,
        dissapear: 2000,
        lastRadius: 0,
    },
    time: {
        starttime: null,
        runtime: null,
    },
    path: {
        radius: null,
        min: null,
        max: null,
    },
    pathRadius: {
        current: null,
        bottom: null,
        ceiling: null,
        mod: 0.15,
    },
    dots: [],
};

// Initialize
let frame = 0;

const selector = ".background--header-home-animation > .background__animation";
const canvas = document.querySelector(`${selector} > canvas`);
const ctx = canvas.getContext("2d");

function updateCanvas() {
    const bbox = document.querySelector(selector).getBoundingClientRect();
    const isMobile = MediaMatcher.matchesMax("sm");
    const isPortrait = bbox.width < bbox.height;
    const viewPortLimit = isPortrait ? bbox.width : bbox.height;

    updateCanvasSize(bbox, canvas);

    settings.mainCircle.radius = viewPortLimit * 0.6;
    settings.ghostCircle.radiusMax = viewPortLimit * 0.75;

    const maxPathRadius = Math.min(viewPortLimit, settings.mainCircle.radius) * 1.25;
    settings.path = {
        radius: maxPathRadius * 0.75,
        min: maxPathRadius * 0.5,
        max: maxPathRadius,
    };

    settings.center = {
        x: bbox.width / 2,
        y: bbox.height / 2 - isMobile ? 250 : 130,
    };
    settings.pathRadius.current = settings.path.radius;
    settings.pathRadius.bottom = settings.path.min;
    settings.pathRadius.ceiling = settings.path.max;
}

let resizeDebounce;
const resize = () => {
    if (resizeDebounce) {
        clearTimeout(resizeDebounce);
    }
    resizeDebounce = setTimeout(() => {
        updateCanvas();
    }, 10);
};

window.addEventListener("resize", resize);
window.addEventListener("orientationchange", resize);

updateCanvas();

// Classes
class Animation {
    constructor(duration, value) {
        this.starttime = null;
        this.runtime = 0;
        this.value = value;
        this.duration = duration;
        this.current = 0;
    }

    // Source: https://gist.github.com/gre/1650294
    easeInOutQuad(t) {
        return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    }

    render() {
        if (!this.starttime) {
            this.starttime = window.performance.now();
        }

        this.runtime = window.performance.now() - this.starttime;
        this.current = this.value * this.easeInOutQuad(Math.min(this.runtime / this.duration, 1));

        return this.current;
    }

    done() {
        return this.current >= this.value;
    }
}

class RedCircle {
    render(x, y) {
        ctx.beginPath();
        ctx.arc(x, y, settings.mainCircle.radius, 0, 2 * Math.PI);
        ctx.lineWidth = 1;
        ctx.strokeStyle = "rgb(210, 56, 52)";
        ctx.stroke();
    }
}

class Dot {
    constructor() {
        this.starttime = window.performance.now();
        this.runtime = 0;
        this.blueCircle = new GhostCircle();

        this.movement = Math.random() > 0.5 ? -0.0015 : 0.0015;

        this.initPositionOnCircle();

        this.appearScale = new Animation(settings.dot.animation, 1);
        this.disappearScale = new Animation(settings.dot.animation, 1);
        this.appearOpacity = new Animation(settings.dot.animation, 1);
        this.stay = new Animation(settings.dot.stay, 1);
    }

    initPositionOnCircle() {
        const { x, y } = settings.mainCircle.pos;
        const rad = radToBetween(x, y, settings.center.x, settings.center.y);

        this.pos = rad + randomFraction(-0.3, 0.3);
    }

    draw(x, y, scale, opacity) {
        ctx.beginPath();
        ctx.arc(x, y, settings.dot.radius * scale, 0, 2 * Math.PI);
        ctx.globalAlpha = opacity;
        ctx.fillStyle = "rgb(210, 56, 52)";
        ctx.fill();
        ctx.globalAlpha = 1;
    }

    animate() {
        const { x, y } = settings.mainCircle.pos;
        const cx = x + settings.mainCircle.radius * Math.cos(this.pos);
        const cy = y + settings.mainCircle.radius * Math.sin(this.pos);

        this.runtime = window.performance.now() - this.starttime;

        this.pos += this.movement;

        const scale = !this.blueCircle.appearScale.done()
            ? this.appearScale.render()
            : this.stay.render() === 1
                ? 1 - this.disappearScale.render()
                : 1;

        this.blueCircle.animate(cx, cy, this.appearScale.done());

        this.draw(cx, cy, scale, this.appearOpacity.render());
    }

    isAlive() {
        return !this.disappearScale.done();
    }
}

class GhostCircle {
    constructor() {
        this.radius = random(settings.ghostCircle.radiusMin, settings.ghostCircle.radiusMax);

        while (
            this.radius <= settings.ghostCircle.lastRadius - 30 &&
            this.radius >= settings.ghostCircle.lastRadius + 30
        ) {
            this.radius = random(settings.ghostCircle.radiusMin, settings.ghostCircle.radiusMax);
        }

        settings.ghostCircle.lastRadius = this.radius;

        this.appearScale = new Animation(settings.ghostCircle.animation, 1);
        this.appearOpacity = new Animation(settings.ghostCircle.animation, 1);
        this.disappearOpacity = new Animation(settings.ghostCircle.dissapear, 1);
    }

    draw(cx, cy, scale, opacity, rad) {
        const radius = this.radius * scale;

        const x0 = cx - Math.cos(rad) * radius;
        const y0 = cy - Math.sin(rad) * radius;
        const x1 = cx + Math.cos(rad) * radius;
        const y1 = cy + Math.sin(rad) * radius;

        if (settings.debug) {
            // Debug gradient angle
            ctx.beginPath();
            ctx.moveTo(x0, y0);
            ctx.lineTo(x1, y1);
            ctx.stroke();
        }

        const gradient = ctx.createLinearGradient(x0, y0, x1, y1);
        gradient.addColorStop(0, "rgba(185, 211, 209, 0)");
        gradient.addColorStop(1, "rgba(185, 211, 209, 0.6)");

        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, 2 * Math.PI);
        ctx.globalAlpha = opacity;
        ctx.fillStyle = gradient;
        ctx.fill();
        ctx.globalAlpha = 1;
    }

    animate(cx, cy, startAnimation) {
        let scale = 0;
        let opacity = 0;

        if (startAnimation) {
            scale = this.appearScale.render();
            opacity = this.appearOpacity.render();

            if (this.appearOpacity.done()) {
                opacity = 1 - this.disappearOpacity.render();
            }
        }

        // calc direction the gradient should face to
        const rad = radToBetween(cx, cy, settings.mainCircle.pos.x, settings.mainCircle.pos.y);

        cx = cx - scale * this.radius * Math.cos(rad);
        cy = cy - scale * this.radius * Math.sin(rad);

        this.draw(cx, cy, scale, opacity, rad);
    }

    isAlive() {
        return !this.disappearOpacity.done();
    }
}

function nextPosition() {
    const rad = valueForFrame(frame, 2 * Math.PI, settings.mainCircle.speed);
    return {
        x: settings.center.x + settings.pathRadius.current * Math.cos(rad),
        y: settings.center.y - settings.pathRadius.current * Math.sin(rad),
    };
}

function updatePathRadius() {
    settings.pathRadius.current += settings.pathRadius.mod;

    if (settings.debug) {
        // Debug path for movement
        ctx.beginPath();
        ctx.arc(settings.center.x, settings.center.y, settings.pathRadius.current, 0, 2 * Math.PI);
        ctx.lineWidth = 1;
        ctx.strokeStyle = "rgb(0, 0, 0)";
        ctx.stroke();

        ctx.font = "30px Arial";
        ctx.fillText(settings.pathRadius.current, settings.center.x, settings.center.y);
        ctx.fillText(settings.pathRadius.ceiling, settings.center.x, settings.center.y + 30);
        ctx.fillText(settings.pathRadius.bottom, settings.center.x, settings.center.y + 60);
    }

    if (settings.pathRadius.current > settings.pathRadius.ceiling) {
        settings.pathRadius.mod *= -1;
        return;
    }

    if (settings.pathRadius.current < settings.pathRadius.bottom) {
        settings.pathRadius.mod *= -1;
    }

    if (settings.debug) {
        // show center
        ctx.beginPath();
        ctx.arc(settings.center.x, settings.center.y, 5, 0, 2 * Math.PI);
        ctx.fillStyle = "rgb(175, 175, 175)";
        ctx.fill();
    }
}

const redCircle = new RedCircle();

const draw = (timestamp) => {
    window.requestAnimationFrame(draw);

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (!settings.time.starttime) {
        settings.time.starttime = timestamp;
    }

    settings.time.runtime = timestamp - settings.time.starttime;
    settings.time.elapsed = timestamp - settings.time.starttime;

    const { x, y } = nextPosition();
    settings.mainCircle.pos = { x, y };
    redCircle.render(x, y);

    frame++;

    updatePathRadius();

    for (const dotNo in settings.dots) {
        if (settings.dots[dotNo]) {
            const dot = settings.dots[dotNo];
            dot.animate();
            if (!dot.isAlive()) {
                delete settings.dots[dotNo];
            }
        }
    }

    if ((frame % settings.newDotEveryNthFrame) + 1 === settings.newDotEveryNthFrame) {
        settings.dots.push(new Dot());
        settings.newDotEveryNthFrame = random(settings.newDotNthFrame.from, settings.newDotNthFrame.to);
    }
};

draw();
